export type RefreshErrorHandler<T> = (error: unknown, request: T) => void

/**
 * Runs one refresh at a time and retains only the latest refresh requested while
 * the current one is in flight. This gives callers a trailing refresh without
 * issuing overlapping requests or dropping a refresh that may contain newer data.
 */
export class LatestRefreshQueue<T> {
    private inFlight = false
    private pending: T | undefined
    private currentDrain: Promise<void> | undefined
    private disposed = false

    constructor(private readonly refresh: (request: T) => Promise<void>, private readonly onError: RefreshErrorHandler<T>) {}

    enqueue(request: T): Promise<void> {
        if (this.disposed) {
            return Promise.resolve()
        }

        if (this.inFlight) {
            this.pending = request
            return this.currentDrain || Promise.resolve()
        }

        this.inFlight = true
        this.currentDrain = this.drain(request)
        return this.currentDrain
    }

    dispose(): void {
        this.disposed = true
        this.pending = undefined
    }

    private async drain(initialRequest: T): Promise<void> {
        let request: T | undefined = initialRequest

        try {
            while (request !== undefined && !this.disposed) {
                this.pending = undefined
                try {
                    await this.refresh(request)
                } catch (error) {
                    if (!this.disposed) {
                        this.onError(error, request)
                    }
                }
                request = this.pending
            }
        } finally {
            this.inFlight = false
            this.currentDrain = undefined
        }
    }
}
